import { oc } from "@orpc/contract";
import { z } from "zod";

// ============================================================================
// Scalar Schemas
// ============================================================================

const UUIDSchema = z.string().uuid();

const SchemaFieldTypeSchema = z.enum([
  "string",
  "text",
  "integer",
  "float",
  "boolean",
  "date",
  "time",
  "datetime",
  "enum",
  "json",
  "range",
  "file",
  "image",
  "url",
  "geospatial",
  "currency",
  "unit",
  "object",
  "array",
]);

const SchemaTypeSchema = z.enum(["extraction", "extension"]);
const SchemaModeSchema = z.enum(["standalone", "extend_base"]);

const ValidationRulesSchema = z
  .object({
    min: z.number().optional(),
    max: z.number().optional(),
    pattern: z.string().optional(),
    minLength: z.number().optional(),
    maxLength: z.number().optional(),
    precision: z.number().optional(),
  })
  .nullable()
  .optional();

const ExtractionHintsSchema = z
  .object({
    aiPromptHint: z.string(),
    exampleValues: z.array(z.string()).optional(),
    synonyms: z.array(z.string()).optional(),
  })
  .nullable()
  .optional();

const FieldUIConfigSchema = z
  .object({
    widget: z
      .enum([
        "text",
        "textarea",
        "select",
        "date-picker",
        "number",
        "toggle",
        "currency",
      ])
      .optional(),
    placeholder: z.string().optional(),
    width: z.enum(["sm", "md", "lg", "full"]).optional(),
    section: z.string().optional(),
  })
  .nullable()
  .optional();

const CompiledExtractionSchemaSummarySchema = z.object({
  schemaDefinitionId: UUIDSchema,
  schemaDefinitionVersion: z.number(),
  schemaDefinitionName: z.string(),
  schemaDefinitionSlug: z.string(),
  provider: z.literal("extend_ai"),
});

const ExtractedDocumentSchema = z.object({
  documentId: UUIDSchema,
  extractionSchemaId: UUIDSchema,
  extractionSchemaVersion: z.number(),
  extractionSchemaName: z.string(),
  extractionSchemaSlug: z.string(),
  provider: z.literal("extend_ai"),
  status: z.literal("completed"),
  extractedPayload: z.record(z.string(), z.unknown()).nullable(),
  processingMetadata: z.record(z.string(), z.unknown()).nullable(),
});

// ============================================================================
// Response Schemas
// ============================================================================

// Recursive field definition schema using z.lazy
const BaseSchemaFieldDefinitionResponseSchema = z.object({
  id: UUIDSchema,
  fieldName: z.string(),
  fieldType: SchemaFieldTypeSchema,
  label: z.string(),
  description: z.string().nullable(),
  isRequired: z.boolean(),
  isMultiple: z.boolean(),
  sortOrder: z.number(),
  coreEnumId: UUIDSchema.nullable(),
  enumValues: z.array(z.string()).nullable().optional(),
  parentFieldId: UUIDSchema.nullable().optional(),
  defaultValue: z.unknown().nullable(),
  validationRules: ValidationRulesSchema,
  extractionHints: ExtractionHintsSchema,
  uiConfig: FieldUIConfigSchema,
});

type BaseSchemaFieldDefinitionResponse = z.infer<
  typeof BaseSchemaFieldDefinitionResponseSchema
>;

export type SchemaFieldDefinitionResponse =
  BaseSchemaFieldDefinitionResponse & {
    children?: SchemaFieldDefinitionResponse[];
  };

export const SchemaFieldDefinitionResponseSchema: z.ZodType<SchemaFieldDefinitionResponse> =
  BaseSchemaFieldDefinitionResponseSchema.extend({
    children: z
      .lazy(() => z.array(SchemaFieldDefinitionResponseSchema))
      .optional(),
  });

const SchemaDefinitionSnapshotSchema = z.object({
  name: z.string(),
  slug: z.string(),
  targetModel: z.string(),
  schemaType: SchemaTypeSchema,
  mode: SchemaModeSchema,
  baseSchemaRef: z.string().nullable().optional(),
  // Free-form on the wire so the frontend can render orphan / deprecated
  // values from older versions without rejecting the response. The strict
  // enum lives at write-time in the backend service.
  documentType: z.string().nullable().optional(),
  keyedBy: z.array(z.string().uuid()).nullable().optional(),
  fields: z.array(SchemaFieldDefinitionResponseSchema),
});

export const SchemaDefinitionVersionResponseSchema = z.object({
  id: UUIDSchema,
  schemaDefinitionId: UUIDSchema,
  version: z.number(),
  snapshot: SchemaDefinitionSnapshotSchema,
  createdBy: UUIDSchema.nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const SchemaDefinitionResponseSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  name: z.string(),
  slug: z.string(),
  targetModel: z.string(),
  schemaType: SchemaTypeSchema,
  mode: SchemaModeSchema,
  baseSchemaRef: z.string().nullable(),
  activeVersionId: UUIDSchema.nullable().optional(),
  version: z.number(),
  isActive: z.boolean(),
  // Tolerated as an arbitrary string so deprecated/unknown values from old
  // rows still round-trip cleanly to the editor's "Deprecated" badge.
  // Write paths (create/update) pin this to the `DocumentType` enum.
  documentType: z.string().nullable().optional(),
  keyedBy: z.array(z.string().uuid()).nullable(),
  createdByUserId: UUIDSchema.nullable().optional(),
  ownedByUserId: UUIDSchema.nullable().optional(),
  fields: z.array(SchemaFieldDefinitionResponseSchema),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const ListSchemaDefinitionsInputSchema = z.object({
  targetModel: z.string().optional(),
  schemaType: SchemaTypeSchema.optional(),
  isActive: z.boolean().optional(),
  // Pass `null` to find schemas with no documentType set (untargeted).
  // Pass a `DocumentType` string to find schemas wired to that classification.
  // Omit to skip the filter entirely.
  documentType: z.string().nullable().optional(),
});

export const GetSchemaDefinitionInputSchema = z.object({
  id: UUIDSchema,
});

export const CreateSchemaDefinitionInputSchema = z.object({
  name: z.string().min(1),
  slug: z.string().min(1),
  targetModel: z.string().min(1),
  schemaType: SchemaTypeSchema,
  mode: SchemaModeSchema,
  baseSchemaRef: z.string().nullable().optional(),
  // Forwarded to the backend service which validates against the
  // `DocumentType` enum and rejects unknowns. Wire-side stays a string so
  // callers don't have to mirror the enum.
  documentType: z.string().nullable().optional(),
  keyedBy: z.array(z.string().uuid()).nullable().optional(),
});

export const UpdateSchemaDefinitionInputSchema = z.object({
  id: UUIDSchema,
  name: z.string().min(1).optional(),
  slug: z.string().min(1).optional(),
  mode: SchemaModeSchema.optional(),
  baseSchemaRef: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
  documentType: z.string().nullable().optional(),
  keyedBy: z.array(z.string().uuid()).nullable().optional(),
});

export const DeleteSchemaDefinitionInputSchema = z.object({
  id: UUIDSchema,
});

export const AddFieldInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  parentFieldId: UUIDSchema.nullable().optional(),
  fieldName: z.string().min(1),
  fieldType: SchemaFieldTypeSchema,
  label: z.string().min(1),
  description: z.string().nullable().optional(),
  isRequired: z.boolean().optional(),
  isMultiple: z.boolean().optional(),
  sortOrder: z.number().int().nonnegative(),
  coreEnumId: UUIDSchema.nullable().optional(),
  enumValues: z.array(z.string()).nullable().optional(),
  defaultValue: z.unknown().nullable().optional(),
  validationRules: ValidationRulesSchema,
  extractionHints: ExtractionHintsSchema,
  uiConfig: FieldUIConfigSchema,
});

export const UpdateFieldInputSchema = z.object({
  id: UUIDSchema,
  schemaDefinitionId: UUIDSchema,
  fieldName: z.string().min(1).optional(),
  fieldType: SchemaFieldTypeSchema.optional(),
  label: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  isRequired: z.boolean().optional(),
  isMultiple: z.boolean().optional(),
  sortOrder: z.number().int().nonnegative().optional(),
  coreEnumId: UUIDSchema.nullable().optional(),
  enumValues: z.array(z.string()).nullable().optional(),
  defaultValue: z.unknown().nullable().optional(),
  validationRules: ValidationRulesSchema,
  extractionHints: ExtractionHintsSchema,
  uiConfig: FieldUIConfigSchema,
});

export const RemoveFieldInputSchema = z.object({
  id: UUIDSchema,
  schemaDefinitionId: UUIDSchema,
});

export const ReorderFieldsInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  parentFieldId: UUIDSchema.nullable().optional(),
  fieldIds: z.array(UUIDSchema),
});

// Recursive node describing a single field to import (with optional children).
const BaseImportFieldNodeSchema = z.object({
  fieldName: z.string().min(1),
  fieldType: SchemaFieldTypeSchema,
  label: z.string().min(1),
  description: z.string().nullable().optional(),
  isRequired: z.boolean(),
  enumValues: z.array(z.string()).nullable().optional(),
  defaultValue: z.unknown().nullable().optional(),
  extractionHints: ExtractionHintsSchema,
});

type BaseImportFieldNode = z.infer<typeof BaseImportFieldNodeSchema>;

export type ImportFieldNode = BaseImportFieldNode & {
  children?: ImportFieldNode[];
};

export const ImportFieldNodeSchema: z.ZodType<ImportFieldNode> =
  BaseImportFieldNodeSchema.extend({
    children: z.lazy(() => z.array(ImportFieldNodeSchema)).optional(),
  });

export const ImportFieldsInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  fields: z.array(ImportFieldNodeSchema).min(1),
});

export const ImportFieldsResponseSchema = z.object({
  insertedCount: z.number().int().nonnegative(),
  schema: SchemaDefinitionResponseSchema,
});

export const PreviewJsonSchemaInputSchema = z.object({
  id: UUIDSchema,
});

export const RunExtractionPreviewInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  documentId: UUIDSchema,
});

// Version request schemas
export const PublishVersionInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
});

export const ListVersionsInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
});

export const GetVersionInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  versionId: UUIDSchema,
});

export const ActivateVersionInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  versionId: UUIDSchema,
});

export const RunExtractionPreviewResponseSchema = z.object({
  compiledExtractionSchema: CompiledExtractionSchemaSummarySchema,
  extractedDocument: ExtractedDocumentSchema,
  warnings: z.array(z.string()),
});

export const AssistSchemaImportFieldsInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  instruction: z.string().min(1).max(16_000),
});

export const AssistEditSchemaResponseSchema = z.object({
  fields: z.array(ImportFieldNodeSchema),
  explanation: z.string(),
});

/** @deprecated Use AssistEditSchemaResponseSchema — assist no longer persists directly. */
export const AssistSchemaImportFieldsResponseSchema = z.object({
  insertedCount: z.number().int().nonnegative(),
  schema: SchemaDefinitionResponseSchema,
  explanation: z.string(),
});

export const SyncFieldsFromImportTreeInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  fields: z.array(ImportFieldNodeSchema),
});

export const SyncFieldsFromImportTreeResponseSchema = z.object({
  insertedCount: z.number().int().nonnegative(),
  updatedCount: z.number().int().nonnegative(),
  deletedCount: z.number().int().nonnegative(),
});

export const PreviewAssistImportFieldsInputSchema = z.object({
  instruction: z.string().min(1).max(16_000),
  existingRootFieldNames: z.array(z.string()).optional().default([]),
});

export const PreviewAssistImportFieldsResponseSchema = z.object({
  fields: z.array(ImportFieldNodeSchema).min(1),
  explanation: z.string(),
});

// ============================================================================
// Contract Definitions
// ============================================================================

export const schemaDefinitionsContracts = {
  list: oc
    .input(ListSchemaDefinitionsInputSchema)
    .output(z.array(SchemaDefinitionResponseSchema))
    .route({ method: "GET" }),

  get: oc
    .input(GetSchemaDefinitionInputSchema)
    .output(SchemaDefinitionResponseSchema)
    .route({ method: "GET" }),

  create: oc
    .input(CreateSchemaDefinitionInputSchema)
    .output(SchemaDefinitionResponseSchema)
    .route({ method: "POST" }),

  update: oc
    .input(UpdateSchemaDefinitionInputSchema)
    .output(SchemaDefinitionResponseSchema)
    .route({ method: "PATCH" }),

  delete: oc
    .input(DeleteSchemaDefinitionInputSchema)
    .output(z.object({ success: z.boolean(), id: UUIDSchema }))
    .route({ method: "DELETE" }),

  addField: oc
    .input(AddFieldInputSchema)
    .output(SchemaFieldDefinitionResponseSchema)
    .route({ method: "POST" }),

  updateField: oc
    .input(UpdateFieldInputSchema)
    .output(SchemaFieldDefinitionResponseSchema)
    .route({ method: "PATCH" }),

  removeField: oc
    .input(RemoveFieldInputSchema)
    .output(z.object({ success: z.boolean(), id: UUIDSchema }))
    .route({ method: "DELETE" }),

  reorderFields: oc
    .input(ReorderFieldsInputSchema)
    .output(z.object({ success: z.boolean() }))
    .route({ method: "POST" }),

  importFields: oc
    .input(ImportFieldsInputSchema)
    .output(ImportFieldsResponseSchema)
    .route({ method: "POST" }),

  assistEditSchema: oc
    .input(AssistSchemaImportFieldsInputSchema)
    .output(AssistEditSchemaResponseSchema)
    .route({ method: "POST" }),

  /** @deprecated Use assistEditSchema */
  assistImportFields: oc
    .input(AssistSchemaImportFieldsInputSchema)
    .output(AssistSchemaImportFieldsResponseSchema)
    .route({ method: "POST" }),

  syncFieldsFromImportTree: oc
    .input(SyncFieldsFromImportTreeInputSchema)
    .output(SyncFieldsFromImportTreeResponseSchema)
    .route({ method: "POST" }),

  previewAssistImportFields: oc
    .input(PreviewAssistImportFieldsInputSchema)
    .output(PreviewAssistImportFieldsResponseSchema)
    .route({ method: "POST" }),

  previewJsonSchema: oc
    .input(PreviewJsonSchemaInputSchema)
    .output(z.object({ jsonSchema: z.record(z.string(), z.unknown()) }))
    .route({ method: "GET" }),

  runExtractionPreview: oc
    .input(RunExtractionPreviewInputSchema)
    .output(RunExtractionPreviewResponseSchema)
    .route({ method: "POST" }),

  publishVersion: oc
    .input(PublishVersionInputSchema)
    .output(SchemaDefinitionVersionResponseSchema)
    .route({ method: "POST" }),

  listVersions: oc
    .input(ListVersionsInputSchema)
    .output(z.array(SchemaDefinitionVersionResponseSchema))
    .route({ method: "GET" }),

  getVersion: oc
    .input(GetVersionInputSchema)
    .output(SchemaDefinitionVersionResponseSchema)
    .route({ method: "GET" }),

  activateVersion: oc
    .input(ActivateVersionInputSchema)
    .output(z.object({ success: z.boolean(), activeVersionId: UUIDSchema }))
    .route({ method: "POST" }),
} as const;

// ============================================================================
// Type Exports
// ============================================================================

export type SchemaDefinitionResponse = z.infer<
  typeof SchemaDefinitionResponseSchema
>;
export type SchemaDefinitionVersionResponse = z.infer<
  typeof SchemaDefinitionVersionResponseSchema
>;
export type ListSchemaDefinitionsInput = z.infer<
  typeof ListSchemaDefinitionsInputSchema
>;
export type CreateSchemaDefinitionInput = z.infer<
  typeof CreateSchemaDefinitionInputSchema
>;
export type UpdateSchemaDefinitionInput = z.infer<
  typeof UpdateSchemaDefinitionInputSchema
>;
export type AddFieldInput = z.infer<typeof AddFieldInputSchema>;
export type UpdateFieldInput = z.infer<typeof UpdateFieldInputSchema>;
export type ImportFieldsInput = z.infer<typeof ImportFieldsInputSchema>;
export type ImportFieldsResponse = z.infer<typeof ImportFieldsResponseSchema>;
export type AssistSchemaImportFieldsInput = z.infer<
  typeof AssistSchemaImportFieldsInputSchema
>;
export type AssistEditSchemaResponse = z.infer<
  typeof AssistEditSchemaResponseSchema
>;
export type SyncFieldsFromImportTreeInput = z.infer<
  typeof SyncFieldsFromImportTreeInputSchema
>;
export type SyncFieldsFromImportTreeResponse = z.infer<
  typeof SyncFieldsFromImportTreeResponseSchema
>;
export type AssistSchemaImportFieldsResponse = z.infer<
  typeof AssistSchemaImportFieldsResponseSchema
>;
export type PreviewAssistImportFieldsInput = z.infer<
  typeof PreviewAssistImportFieldsInputSchema
>;
export type PreviewAssistImportFieldsResponse = z.infer<
  typeof PreviewAssistImportFieldsResponseSchema
>;
export type RunExtractionPreviewInput = z.infer<
  typeof RunExtractionPreviewInputSchema
>;
export type RunExtractionPreviewResponse = z.infer<
  typeof RunExtractionPreviewResponseSchema
>;
